import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import LoadingButton from '@mui/lab/LoadingButton'
import { Grid, Typography, Button, Avatar, Paper, Box } from '@mui/material'
import { sanitize } from 'dompurify'
import GenericTextField from 'components/UserFields/GenericTextField'
import StateField from 'components/UserFields/StateField'
import { UploadPhotoButton } from 'pages/Users/Profile/Buttons'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import { fetchUpdateEmployerUser } from 'src/redux/employerUser'
import PhoneNumberField from 'components/UserFields/PhoneNumberField'
import { TextEditor } from 'components/TextEditor/TextEditor'
import GenericSwitchField from 'components/UserFields/GenericSwitchField'
import { getProfileImage } from '../../Jobs/helpers'

const AboutYou = ({ employerUser }) => {
  const user = useSelector(state => state.user)
  user.type = 'employerUser'
  const { loading, id } = employerUser
  user.id = id
  const [readOnly, setReadOnly] = React.useState(true)
  const [values, setValues] = React.useState({})
  const dispatch = useDispatch()
  const imageURL = getProfileImage(employerUser.profileImage)
  const setDefaultValues = () => {
    setValues({
      firstName: employerUser.firstName,
      lastName: employerUser.lastName,
      email: employerUser.email,
      phone: employerUser.phone,
      city: employerUser.city,
      state: employerUser.state,
      about: employerUser.about,
      showPhone: employerUser.showPhone,
      showEmail: employerUser.showEmail,
    })
  }
  React.useEffect(() => {
    if (!employerUser.loading) {
      setDefaultValues()
    }
  }, [employerUser])

  const handleEditClick = () => {
    setReadOnly(false)
  }
  const handleCancelClick = () => {
    setDefaultValues()
    setReadOnly(true)
  }

  const handleSubmit = () => {
    const data = {
      ...values,
      id: employerUser.id,
    }
    dispatch(
      fetchUpdateEmployerUser(data, 'Updated Basic Info', null, {
        function: setReadOnly,
        params: true,
      }),
    )
  }

  const handleChange = event => {
    setValues({ ...values, [event.target.name]: event.target.value })
  }

  const handleEditorChange = event => {
    const { about, aboutRaw } = event
    setValues({ ...values, about: about, aboutRaw: aboutRaw })
  }

  return (
    <Grid
      container
      direction='column'
      justifyContent='flex-start'
      sx={{
        marginBottom: '50px',
        backgroundColor: 'transparent',
        padding: '50px'
      }}
    >
      <Grid container direction='row' justifyContent='space-between' item xs={12}>
        <Grid container item justifyContent='flex-end'>
          {readOnly ? (
            <Button
              variant='contained'
              size='small'
              onClick={handleEditClick}
              startIcon={<EditIcon />}
            >
              Edit Info
            </Button>
          ) : (
            <Grid item>
              <Button size='small' sx={{ marginRight: '20px' }} onClick={handleCancelClick}>
                Cancel
              </Button>
              <LoadingButton
                variant='contained'
                size='small'
                onClick={handleSubmit}
                startIcon={<SaveIcon />}
                loading={loading}
              >
                Save Changes
              </LoadingButton>
            </Grid>
          )}
        </Grid>

        {/* Profile Photo Section */}
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.default',
              mb: 3
            }}
          >
            <Grid
              container
              direction='row'
              justifyContent='flex-start'
              alignItems='center'
              spacing={2}
            >
              <Grid item>
                <Avatar src={imageURL} sx={{ width: 100, height: 100, marginBottom: '10px' }} />
              </Grid>
              <Grid item>
                {readOnly ? (
                  <Typography variant='h6'>
                    {employerUser.firstName} {employerUser.lastName}
                  </Typography>
                ) : (
                  <Grid container spacing={2} item>
                    <Grid item xs={12} md={6}>
                      <GenericTextField
                        name='firstName'
                        label='First Name'
                        value={values.firstName}
                        disabled={readOnly}
                        handleChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <GenericTextField
                        name='lastName'
                        label='Last Name'
                        value={values.lastName}
                        disabled={readOnly}
                        handleChange={handleChange}
                      />
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Box sx={{ mt: 2 }}>
              <UploadPhotoButton user={user} />
            </Box>
          </Paper>
        </Grid>

        {/* Contact Information Section */}
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.default',
              mb: 3
            }}
          >
            <Typography variant="subtitle1" sx={{ mb: 3, fontWeight: 500 }}>
              Contact Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <GenericTextField value={values.email} disabled={true} label='Email' name='email' />
              </Grid>
              <Grid item xs={12} md={6}>
                <PhoneNumberField
                  value={values.phone}
                  disabled={readOnly}
                  handleChange={handleChange}
                  label='Phone Number'
                  name='phone'
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <GenericTextField
                  name='city'
                  label='City'
                  value={values.city}
                  disabled={readOnly}
                  handleChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <StateField
                  name='state'
                  label='State'
                  value={values.state}
                  disabled={readOnly}
                  handleChange={handleChange}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Privacy Settings Section */}
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.default',
              mb: 3
            }}
          >
            <Typography variant="subtitle1" sx={{ mb: 3, fontWeight: 500 }}>
              Privacy Settings
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <GenericSwitchField
                  name='showEmail'
                  label='Display Email'
                  value={values.showEmail}
                  disabled={readOnly}
                  handleChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <GenericSwitchField
                  name='showPhone'
                  label='Display Phone Number'
                  value={values.showPhone}
                  disabled={readOnly}
                  handleChange={handleChange}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* About You Section */}
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.default'
            }}
          >
            <Typography variant="subtitle1" sx={{ mb: 3, fontWeight: 500 }}>
              About You
            </Typography>
            {readOnly ? (
              <Typography
                variant='body1'
                dangerouslySetInnerHTML={{ __html: sanitize(values.about) }}
              />
            ) : (
              <TextEditor initialValue={values.about} onChange={handleEditorChange} />
            )}
          </Paper>
        </Grid>
      </Grid>
    </Grid>
  )
}
export default AboutYou
